/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { DescriptionPage_CourseType as CourseType } from '__generated__/graphql-types';
import { compose } from 'recompose';

import { useLocation, useParams } from 'js/lib/useRouter';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import Naptime from 'bundles/naptimejs';
import type { InjectedNaptime } from 'bundles/naptimejs';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import EnterpriseCourseEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/enterprise/EnterpriseCourseEnrollButtonNotConfigurable';
import LoadingOverlay from 'bundles/unified-description-page-common/components/loading/LoadingOverlay';
import MobileCtaModule from 'bundles/unified-description-page-common/components/modules/mobile-cta/MobileCtaModule';
import FixedPageNavigation from 'bundles/unified-description-page-common/components/modules/page-navigation/FixedPageNavigation';
import NavCoBrandingPartnerLogos from 'bundles/unified-description-page-common/components/modules/page-navigation/NavCoBrandingPartnerLogos';
import NavPartnerLogo from 'bundles/unified-description-page-common/components/modules/page-navigation/NavPartnerLogo';
import CoursePageSEO from 'bundles/unified-description-page-common/components/seo/CoursePageSEO';
import { useEnterpriseCoursePageData } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseBffCourseContext';
import { useEnterpriseLearnerFlowOptimization } from 'bundles/unified-description-page-common/contexts/enterprise/EnterpriseLearnerFlowOptimizationContext';
import { useAutoContinueWithCourse } from 'bundles/unified-description-page-common/hooks/useContinueWithCourse';
import useLogRecentlyViewed from 'bundles/unified-description-page-common/hooks/useRecentlyViewed';
import useSwitchToCurrentProgram from 'bundles/unified-description-page-common/hooks/useSwitchToCurrentProgram';
import Renderer from 'bundles/unified-description-page-common/pages/enterprise/course/EnterpriseCoursePageRenderer';
import EventingDataProvider from 'bundles/unified-description-page-common/providers/EventingDataProvider';
import LanguageInfoDialogProvider from 'bundles/unified-description-page-common/providers/LanguageInfoDialogProvider';
import PageInfoProvider from 'bundles/unified-description-page-common/providers/PageInfoProvider';
import PageNavigationProvider from 'bundles/unified-description-page-common/providers/PageNavigationProvider';
import StickyPanelProvider from 'bundles/unified-description-page-common/providers/StickyPanelProvider';
import { ModuleNames } from 'bundles/unified-description-page-common/types/moduleConfig';
import { showAiDubbingOnXdp } from 'bundles/unified-description-page-common/utils/dubbingUtil';
import {
  COURSE_NAV_ITEMS,
  getCoursePageNavigationLinks,
} from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getPartnersForCourse from 'bundles/unified-description-page-common/utils/getPartnersForCourse';
import getProductId from 'bundles/unified-description-page-common/utils/getProductId';
import { getEnterpriseRouteForCourseProducts } from 'bundles/unified-description-page-common/utils/getRouteForCourseProducts';
import {
  applyStreamlinedXDPModuleLayout,
  isStreamlinedXDP,
} from 'bundles/unified-description-page-common/utils/useStreamlinedXDP';

import _t from 'i18n!nls/xdp';

type Props = {
  naptime: InjectedNaptime;
};

const EnterpriseCoursePageRootContent = ({ naptime }: Props) => {
  const pageData = useEnterpriseCoursePageData();
  const { search, query } = useLocation();
  const { resourcePath, isLoading: isLoadingAutoContinue } = useAutoContinueWithCourse(
    query.isExternal,
    pageData?.slug
  );
  const { isEnrolling } = useEnterpriseLearnerFlowOptimization();
  const shouldRedirectToNextItem = !!resourcePath;
  const features = useEnterpriseProductFeatures();
  const { productSlug: productSlugInUrl, programSlug } = useParams();
  useSwitchToCurrentProgram();

  // Redirect to next item in the course if available
  React.useLayoutEffect(() => {
    if (resourcePath) {
      redirectClientOrServer(resourcePath);
    }
  }, [resourcePath]);

  useLogRecentlyViewed({
    name: pageData?.name,
    slug: pageData?.slug,
    partners: pageData?.partners,
    // @ts-expect-error URL: string
    photoUrl: pageData?.photoUrl,
    id: getProductId('course', pageData?.id),
    difficulty: pageData?.difficultyLevel,
    productType: pageData?.courseType,
  });

  if (!pageData) {
    return null;
  } else if (
    !!pageData.courseType &&
    (pageData.courseType !== CourseType.StandardCourse || productSlugInUrl !== pageData.slug)
  ) {
    redirectClientOrServer(
      getEnterpriseRouteForCourseProducts(pageData.slug, programSlug, pageData.courseType, search)
    );
  }

  const partnersToDisplay = getPartnersForCourse(pageData.partners, pageData.activeSpecialization);

  const productTitle = getProductTitle(pageData.name);

  const pageConfig = {
    modules: [
      { name: ModuleNames.Breadcrumbs },
      {
        name: ModuleNames.ProductOverview,
      },
      { name: ModuleNames.ThinBanner },
      {
        name: ModuleNames.CareerOutcomes,
      },
      {
        name: ModuleNames.CareerCertificate,
      },
      {
        name: ModuleNames.LearnerIncentive,
      },
      {
        name: ModuleNames.Syllabus,
      },
      {
        name: ModuleNames.Testimonials,
      },
      {
        name: ModuleNames.Reviews,
      },
      {
        name: ModuleNames.Recommendations,
      },
      {
        name: ModuleNames.Upsell,
      },
    ],
  };

  // impress epic only on XDP that has AI dubbing
  const shouldShowAiDubbingOnXdp = !!pageData.dubbedLanguages?.length && showAiDubbingOnXdp();

  // impress epic only on XDPs without AI dubbing
  const isMWebStreamlinedXDP = !pageData.dubbedLanguages?.length && isStreamlinedXDP();

  if (isMWebStreamlinedXDP) {
    pageConfig.modules = applyStreamlinedXDPModuleLayout(pageConfig.modules);
  }

  const navigationLinks = getCoursePageNavigationLinks({
    pageData,
    pageConfig,
  });

  const enrollButtonSmall = (
    <EnterpriseCourseEnrollButtonNotConfigurable
      courseType={pageData.courseType}
      productId={pageData.id}
      slug={pageData.slug}
      size="small"
      // eslint-disable-next-line no-restricted-syntax
      buttonLabel={_t('Go To Course')}
    />
  );
  // Redirection would be in progress if resourcePath is available
  // Show a loader if redirection is in progress with optimised learner flow enabled
  if (features?.enableOptimizedLearnerFlow && (isLoadingAutoContinue || shouldRedirectToNextItem || isEnrolling)) {
    return <LoadingOverlay />;
  }

  return (
    <PageInfoProvider
      programSlug={programSlug}
      navigationIdMap={COURSE_NAV_ITEMS}
      productSlug={pageData.slug}
      hasDubbedLanguages={shouldShowAiDubbingOnXdp}
    >
      <EventingDataProvider productId={pageData.id} productType="course">
        <PageNavigationProvider navigationLinks={navigationLinks}>
          <StickyPanelProvider>
            <LanguageInfoDialogProvider
              languages={{
                primaryLanguages: pageData.primaryLanguages,
                subtitleLanguages: pageData.subtitleLanguages ?? [],
                translatedLanguages: pageData.translatedLanguages ?? [],
                dubbedLanguages: shouldShowAiDubbingOnXdp ? pageData.dubbedLanguages : null,
              }}
            >
              <FixedPageNavigation
                productTitle={productTitle}
                navigationLinks={navigationLinks}
                pageCta={enrollButtonSmall}
                partnerLogo={
                  partnersToDisplay?.length === 2 ? (
                    <NavCoBrandingPartnerLogos
                      // @ts-expect-error URL: string
                      leftImageSrc={partnersToDisplay[0].squareLogo}
                      leftPartnerName={partnersToDisplay[0].name}
                      // @ts-expect-error URL: string
                      rightImageSrc={partnersToDisplay[1].squareLogo}
                      rightPartnerName={partnersToDisplay[1].name}
                    />
                  ) : (
                    <NavPartnerLogo
                      // @ts-expect-error URL: string
                      imageSrc={partnersToDisplay?.[0].squareLogo}
                      partnerName={partnersToDisplay?.[0].name}
                    />
                  )
                }
              />
              <Renderer pageData={pageData} pageConfig={pageConfig} naptime={naptime} />
              <MobileCtaModule pageCta={enrollButtonSmall} />
              <CoursePageSEO pageData={pageData} disableCrawlerIndexing />
            </LanguageInfoDialogProvider>
          </StickyPanelProvider>
        </PageNavigationProvider>
      </EventingDataProvider>
    </PageInfoProvider>
  );
};

// Need naptime instance for enterprise save functionality.
export default compose<Props, {}>(Naptime.createContainer(() => ({})))(EnterpriseCoursePageRootContent);
